import React from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { Paper, Typography, useTheme, useMediaQuery, Box, Avatar, Rating, Divider } from '@mui/material';
import { FormatQuote } from '@mui/icons-material';
import { styled } from '@mui/system';

// Custom styled component for the carousel container
const TestimonialContainer = styled('div')(({ theme }) => ({
  padding: '3rem 0',
  position: 'relative',
  '& .slick-dots': {
    bottom: '-40px',
  },
  '& .slick-dots li button:before': {
    fontSize: '12px',
    color: 'white',
    opacity: 0.4,
  },
  '& .slick-dots li.slick-active button:before': {
    opacity: 1,
    color: 'white',
  },
  '& .slick-prev, & .slick-next': {
    width: '40px',
    height: '40px',
    zIndex: 1,
    background: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '50%',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.3)',
    },
    '&:before': {
      fontSize: '24px',
    }
  },
  '& .slick-prev': {
    left: '-50px',
  },
  '& .slick-next': {
    right: '-50px',
  }
}));

const QuoteIcon = styled(FormatQuote)(({ theme }) => ({
  fontSize: '40px',
  opacity: 0.4,
  color: 'white'
}));

// Testimonial data
const testimonials = [
  {
    name: "Kurizu",
    role: "Creator of Uben",
    avatar: null,
    rating: 5,
    text: "I created Uben because I loved Renshuu for Japanese learning but wanted something similar for German and other languages. Renshuu's MCQ-based SRS system is superior to Anki's binary approach, and that's what I've implemented in Uben for all languages.",
    location: "Creator's Note"
  },
  {
    name: "Pocky",
    role: "German Student",
    avatar: null,
    rating: 5,
    text: "I was looking for a platform like Renshuu but for German. When I found Uben, it was exactly what I needed - the MCQ format for spaced repetition makes learning feel more engaging than just flashcards, and the progress tracking keeps me motivated.",
    location: "Early User"
  },
  {
    name: "Ceasonal",
    role: "Language Enthusiast",
    avatar: null,
    rating: 4,
    text: "The best part about Uben is that it lets me study multiple languages in one place. I used to use different apps for different languages, but now I can track my progress across all my language studies in a single dashboard.",
    location: "Multilingual Learner"
  },
  {
    name: "Alguien",
    role: "Language Learner",
    avatar: null,
    rating: 4,
    text: "yw! tbh I do think multiple choice cards can be nice but also they are extremely painful to setup in Anki so that is something your app has going on",
    location: "Community Feedback"
  },
];

const Testimonials = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    
    const settings = {
        dots: true,
        infinite: true,
        speed: 800,
        autoplay: true,
        autoplaySpeed: 8000,
        slidesToShow: isTablet ? 1 : 2,
        slidesToScroll: 1,
        adaptiveHeight: false,
        centerMode: false,
        arrows: !isMobile,
        pauseOnHover: true,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: 1,
                    arrows: true,
                }
            },
            {
                breakpoint: 600,
                settings: {
                    slidesToShow: 1,
                    arrows: false,
                }
            }
        ]
    };

    const testimonialStyle = {
        padding: isMobile ? "1.5rem" : "2rem",
        backgroundColor: "rgba(255, 255, 255, 0.08)",
        color: "white",
        borderRadius: "16px",
        boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
        backdropFilter: "blur(5px)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        margin: "1rem",
        height: "100%",
        display: "flex",
        flexDirection: "column"
    };

    return (
        <TestimonialContainer>
            <Box sx={{ textAlign: 'center', mb: 5, px: 2 }}>
                <Typography variant="h3" sx={{ 
                    fontWeight: "700", 
                    color: "#FFFFFF", 
                    mb: 2,
                    fontSize: { xs: '2rem', md: '2.5rem' }
                }}>
                    The Story Behind Uben
                </Typography>
                <Typography variant="body1" sx={{ 
                    color: 'rgba(255, 255, 255, 0.7)', 
                    maxWidth: '700px', 
                    margin: '0 auto',
                    fontSize: { xs: '1rem', md: '1.1rem' }
                }}>
                    Inspired by Renshuu's excellent Japanese learning system, Uben brings similar MCQ-based spaced repetition to all languages
                </Typography>
            </Box>

            <Box sx={{ mx: { xs: 2, md: 8 } }}>
                <Slider {...settings}>
                    {testimonials.map((testimonial, index) => (
                        <div key={index}>
                            <Paper elevation={0} sx={testimonialStyle}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                        <QuoteIcon />
                                        <Rating name="read-only" value={testimonial.rating} readOnly 
                                            sx={{ 
                                                '& .MuiRating-iconFilled': { color: '#FFD700' },
                                                '& .MuiRating-iconEmpty': { color: 'rgba(255, 255, 255, 0.3)' }
                                            }}
                                        />
                                    </Box>
                                    
                                    <Typography variant="body1" sx={{ 
                                        flexGrow: 1,
                                        mb: 4,
                                        fontStyle: 'italic',
                                        lineHeight: 1.7,
                                        fontSize: { xs: '0.95rem', md: '1rem' },
                                        color: 'rgba(255, 255, 255, 0.9)'
                                    }}>
                                        "{testimonial.text}"
                                    </Typography>
                                    
                                    <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', mb: 2 }} />
                                    
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Avatar 
                                            sx={{ 
                                                bgcolor: `hsl(${index * 60}, 70%, 60%)`,
                                                width: 50,
                                                height: 50,
                                                mr: 2
                                            }}
                                        >
                                            {testimonial.name.charAt(0)}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                {testimonial.name}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                                {testimonial.role}, {testimonial.location}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Paper>
                        </div>
                    ))}
                </Slider>
            </Box>
        </TestimonialContainer>
    );
};

export default Testimonials;
